import { cajaApi, statsApi } from '@gl/api-client';
import type { ListClosesQuery, ListMovementsQuery } from '@gl/shared';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type { CatalogResponse } from './catalog-types';

export const caja = cajaApi(api);
export const stats = statsApi(api);

export const cajaKeys = {
  sales: (localId: string) => [...queryKeys.local(localId), 'sales'] as const,
  salesOf: (localId: string, businessDate: string) => [...queryKeys.local(localId), 'sales', businessDate] as const,
  cash: (localId: string) => [...queryKeys.local(localId), 'cash'] as const,
  today: (localId: string) => [...queryKeys.local(localId), 'cash', 'today'] as const,
  closes: (localId: string, q: ListClosesQuery = {}) => [...queryKeys.local(localId), 'cash', 'closes', q] as const,
  close: (localId: string, closeId: string) => [...queryKeys.local(localId), 'cash', 'close', closeId] as const,
  expenses: (localId: string, q: ListMovementsQuery = {}) => [...queryKeys.local(localId), 'cash', 'expenses', q] as const,
  incomes: (localId: string, q: ListMovementsQuery = {}) => [...queryKeys.local(localId), 'cash', 'incomes', q] as const,
  stats: (localId: string) => [...queryKeys.local(localId), 'stats'] as const,
  catalog: (localId: string) => [...queryKeys.local(localId), 'catalog', 'active'] as const,
};

/** Después de vender, anular, cargar un movimiento o cerrar: todo lo que depende de la caja. */
export function invalidateCaja(qc: QueryClient, localId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: cajaKeys.sales(localId) }),
    qc.invalidateQueries({ queryKey: cajaKeys.cash(localId) }),
    qc.invalidateQueries({ queryKey: cajaKeys.stats(localId) }),
  ]);
}

export function useInvalidateCaja(localId: string) {
  const qc = useQueryClient();
  return () => invalidateCaja(qc, localId);
}

export function useCashToday(localId: string) {
  return useQuery({
    queryKey: cajaKeys.today(localId),
    queryFn: () => caja.today(localId),
    refetchInterval: 60_000,
  });
}

export function useActiveCatalog(localId: string) {
  return useQuery({
    queryKey: cajaKeys.catalog(localId),
    queryFn: () => api.get<CatalogResponse>(`/locals/${encodeURIComponent(localId)}/catalog?activeOnly=1`),
    staleTime: 5 * 60_000,
  });
}
